import { takeEvery, select, put } from "redux-saga/effects";

import { prefixes, typesFor } from "./types";
import { selectorsFor } from "./selectors";
import { actionsFor } from "./actions";
import { prefixed } from "utils";

const DEFAULT_START_PAGE = 1;

const any = (...args) => args.some(x => !!x);

export function* computePages(action) {
  const {
    getActivePage,
    getStartPage,
    getEndPage,
    getTotalPages,
    getVisiblePages
  } = selectorsFor(action.meta.domain);

  const startPage = yield select(getStartPage);
  const endPage = yield select(getEndPage);
  const totalPages = yield select(getTotalPages);
  const visiblePages = yield select(getVisiblePages);

  const { RESET_PAGE, CHANGE_DISPLAYING } = typesFor(action.meta.domain);
  const shouldResetActivePage = [RESET_PAGE, CHANGE_DISPLAYING].includes(
    action.type
  );
  const activePage = shouldResetActivePage ? 1 : yield select(getActivePage);

  const isEdge = activePage === startPage || activePage === endPage;
  const isOutOfSight = activePage < startPage || activePage > endPage;
  const nullPageRange = startPage === null || endPage === null;

  if (!any(isEdge, isOutOfSight, nullPageRange)) return;

  const startOffset = 2; // Show a few pages before activePage
  let newStartPage = Math.max(activePage - startOffset, 1);
  let newEndPage = newStartPage + visiblePages - 1;

  const remainingPages = Math.min(visiblePages - 1, totalPages - newStartPage);
  if (remainingPages < visiblePages - 1) {
    newStartPage = Math.max(totalPages - visiblePages + 1, 1);
    newEndPage = totalPages;
  }

  const { setStartPage, setEndPage } = actionsFor(action.meta.domain);
  yield put(setStartPage(newStartPage));
  yield put(setEndPage(newEndPage));
}

export function* computeHasNextPrev(action) {
  const { getActivePage, getTotalPages } = selectorsFor(action.meta.domain);
  const activePage = yield select(getActivePage);
  const totalPages = yield select(getTotalPages);

  const newHasPrevPage = activePage > DEFAULT_START_PAGE;
  const newHasNextPage = activePage < totalPages;

  const { hasNextPage, hasPrevPage } = actionsFor(action.meta.domain);
  yield put(hasNextPage(newHasNextPage));
  yield put(hasPrevPage(newHasPrevPage));
}

export function* goNextPage(action) {
  const { getActivePage } = selectorsFor(action.meta.domain);
  const { changePage } = actionsFor(action.meta.domain);

  const activePage = yield select(getActivePage);
  yield put(changePage(activePage + 1));
}

export function* goPrevPage(action) {
  const { getActivePage } = selectorsFor(action.meta.domain);
  const { changePage } = actionsFor(action.meta.domain);

  const activePage = yield select(getActivePage);
  yield put(changePage(activePage - 1));
}

export function* goFirstPage(action) {
  const { changePage } = actionsFor(action.meta.domain);
  yield put(changePage(DEFAULT_START_PAGE));
}

export function* goLastPage(action) {
  const { getTotalPages } = selectorsFor(action.meta.domain);
  const { changePage } = actionsFor(action.meta.domain);

  const lastPage = yield select(getTotalPages);
  yield put(changePage(lastPage));
}

const sagas = [
  takeEvery(pagesChange, computePages),
  takeEvery(pagesChange, computeHasNextPrev),
  takeEvery(prefixed(prefixes.GO_NEXT_PAGE), goNextPage),
  takeEvery(prefixed(prefixes.GO_PREV_PAGE), goPrevPage),
  takeEvery(prefixed(prefixes.GO_FIRST_PAGE), goFirstPage),
  takeEvery(prefixed(prefixes.GO_LAST_PAGE), goLastPage)
];

function pagesChange(action) {
  return any(
    prefixed(prefixes.CHANGE_PAGE)(action),
    prefixed(prefixes.CHANGE_DISPLAYING)(action),
    prefixed(prefixes.SET_TOTAL_ROWS)(action),
    prefixed(prefixes.RESET_PAGE)(action)
  );
}

export default sagas;
